// ============================================================================
// Member Data Context
// Shared state management for member data with client-side pagination and
// lazy loading of member details (vehicles, reservations)
// ============================================================================

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from 'react';

import {
  MemberDataState,
  MemberDataAction,
  MemberDataContextValue,
  MemberListItem,
  MemberDetails,
  MemberFilters,
  MemberSortKey,
} from '../types/memberData';

import { Member } from '../types';

import {
  getMembers,
  getMemberById,
  getPassengerVehicles,
  getRecreationalVehicles,
  mapApiMember,
  mapApiPassengerVehicle,
  mapApiRecreationalVehicle,
} from '../services/memberService';

import {
  getReservationsByMemberId,
  mapApiToGridReservations,
} from '../services/reservationService';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 50;

// ============================================================================
// Initial State
// ============================================================================

const initialState: MemberDataState = {
  // Members list
  members: [],
  membersLoading: false,
  membersError: null,
  membersLoaded: false,

  // Member details cache
  memberDetailsCache: new Map(),
  loadingDetails: new Set(),

  // Pagination (client-side)
  pagination: {
    page: 1,
    pageSize: PAGE_SIZE,
    hasMore: true,
    totalCount: 0,
  },

  // Filters
  filters: {
    search: '',
    bannedOnly: undefined,
  },

  // Sort
  sort: {
    key: 'name',
    direction: 'asc',
  },
};

// ============================================================================
// Reducer
// ============================================================================

function memberDataReducer(state: MemberDataState, action: MemberDataAction): MemberDataState {
  switch (action.type) {
    // Members list actions
    case 'MEMBERS_LOADING':
      return {
        ...state,
        membersLoading: true,
        membersError: null,
      };

    case 'MEMBERS_LOADED':
      return {
        ...state,
        members: action.payload.members,
        membersLoading: false,
        membersError: null,
        membersLoaded: true,
        pagination: {
          ...state.pagination,
          page: 1,
          totalCount: action.payload.totalCount,
          hasMore: action.payload.totalCount > PAGE_SIZE,
        },
      };

    case 'MEMBERS_ERROR':
      return {
        ...state,
        membersLoading: false,
        membersError: action.payload,
      };

    // Member details actions
    case 'MEMBER_DETAILS_LOADING': {
      const newLoading = new Set(state.loadingDetails);
      newLoading.add(action.payload);
      return {
        ...state,
        loadingDetails: newLoading,
      };
    }

    case 'MEMBER_DETAILS_LOADED': {
      const newCache = new Map(state.memberDetailsCache);
      newCache.set(action.payload.memberId, action.payload.details);
      const newLoading = new Set(state.loadingDetails);
      newLoading.delete(action.payload.memberId);
      return {
        ...state,
        memberDetailsCache: newCache,
        loadingDetails: newLoading,
      };
    }

    case 'MEMBER_DETAILS_ERROR': {
      const newLoading = new Set(state.loadingDetails);
      newLoading.delete(action.payload.memberId);
      return {
        ...state,
        loadingDetails: newLoading,
      };
    }

    case 'MEMBER_VEHICLES_LOADED': {
      const cached = state.memberDetailsCache.get(action.payload.memberId);
      if (!cached) return state;
      const newCache = new Map(state.memberDetailsCache);
      newCache.set(action.payload.memberId, {
        ...cached,
        passengerVehicles: action.payload.passengerVehicles,
        recreationalVehicles: action.payload.recreationalVehicles,
        vehiclesLoaded: true,
      });
      return {
        ...state,
        memberDetailsCache: newCache,
      };
    }

    case 'MEMBER_RESERVATIONS_LOADED': {
      const cached = state.memberDetailsCache.get(action.payload.memberId);
      if (!cached) return state;
      const newCache = new Map(state.memberDetailsCache);
      newCache.set(action.payload.memberId, {
        ...cached,
        reservations: action.payload.reservations,
        reservationsLoaded: true,
      });
      return {
        ...state,
        memberDetailsCache: newCache,
      };
    }

    // Cache invalidation actions
    case 'INVALIDATE_MEMBER_DETAILS': {
      const newCache = new Map(state.memberDetailsCache);
      newCache.delete(action.payload);
      return {
        ...state,
        memberDetailsCache: newCache,
      };
    }

    case 'INVALIDATE_ALL_MEMBER_DETAILS':
      return {
        ...state,
        memberDetailsCache: new Map(),
      };

    case 'INVALIDATE_MEMBERS':
      return {
        ...state,
        members: [],
        membersLoaded: false,
        memberDetailsCache: new Map(),
        pagination: {
          ...state.pagination,
          page: 1,
          hasMore: true,
          totalCount: 0,
        },
      };

    // CRUD notification actions
    case 'MEMBER_CREATED':
      return {
        ...state,
        members: [action.payload, ...state.members],
        pagination: {
          ...state.pagination,
          totalCount: state.pagination.totalCount + 1,
        },
      };

    case 'MEMBER_UPDATED': {
      const newCache = new Map(state.memberDetailsCache);
      newCache.delete(action.payload);
      return {
        ...state,
        memberDetailsCache: newCache,
        membersLoaded: false, // Force reload to get updated data
      };
    }

    case 'MEMBER_DELETED': {
      const newCache = new Map(state.memberDetailsCache);
      newCache.delete(action.payload);
      return {
        ...state,
        members: state.members.filter((m) => m.id !== action.payload),
        memberDetailsCache: newCache,
        pagination: {
          ...state.pagination,
          totalCount: Math.max(0, state.pagination.totalCount - 1),
        },
      };
    }

    // Filter/sort actions
    case 'SET_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
        // Reset pagination when filters change
        pagination: {
          ...state.pagination,
          page: 1,
        },
      };

    case 'CLEAR_FILTERS':
      return {
        ...state,
        filters: {
          search: '',
          bannedOnly: undefined,
        },
        pagination: {
          ...state.pagination,
          page: 1,
        },
      };

    case 'SET_SORT': {
      const newDirection =
        state.sort.key === action.payload && state.sort.direction === 'asc' ? 'desc' : 'asc';
      return {
        ...state,
        sort: {
          key: action.payload,
          direction: newDirection,
        },
      };
    }

    // Pagination actions
    case 'SET_PAGE':
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page: action.payload,
        },
      };

    case 'LOAD_MORE_MEMBERS': {
      const nextPage = state.pagination.page + 1;
      const maxPage = Math.ceil(state.pagination.totalCount / PAGE_SIZE);
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page: Math.min(nextPage, maxPage),
          hasMore: nextPage < maxPage,
        },
      };
    }

    case 'RESET_PAGINATION':
      return {
        ...state,
        pagination: {
          ...state.pagination,
          page: 1,
        },
      };

    default:
      return state;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Transform API member to list item (light-weight)
 */
function mapMemberToListItem(member: Member): MemberListItem {
  return {
    id: member.id,
    corporateId: member.corporateId,
    username: member.username,
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
    phone: member.phone,
    banned: member.banned,
    lastLoginDate: member.lastLoginDate,
    createdAt: member.createdAt,
  };
}

/**
 * Apply filters to members list
 */
function applyFilters(members: MemberListItem[], filters: MemberFilters): MemberListItem[] {
  return members.filter((member) => {
    // Search filter (name, email, username, phone)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
      const matchesSearch =
        fullName.includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower) ||
        member.username.toLowerCase().includes(searchLower) ||
        member.phone.includes(filters.search);
      if (!matchesSearch) return false;
    }

    // Banned filter
    if (filters.bannedOnly && !member.banned) {
      return false;
    }

    return true;
  });
}

/**
 * Apply sorting to members list
 */
function applySort(
  members: MemberListItem[],
  sort: { key: MemberSortKey; direction: 'asc' | 'desc' }
): MemberListItem[] {
  const sorted = [...members];
  const direction = sort.direction === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (sort.key) {
      case 'name':
        comparison = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
        break;
      case 'email':
        comparison = a.email.localeCompare(b.email);
        break;
      case 'username':
        comparison = a.username.localeCompare(b.username);
        break;
      case 'phone':
        comparison = (a.phone || '').localeCompare(b.phone || '');
        break;
      case 'createdAt':
        comparison = (a.createdAt || '').localeCompare(b.createdAt || '');
        break;
      case 'lastLoginDate':
        comparison = (a.lastLoginDate || '').localeCompare(b.lastLoginDate || '');
        break;
      case 'status':
        // Sort banned (true) after active (false) in ascending order
        comparison = (a.banned === b.banned) ? 0 : (a.banned ? 1 : -1);
        break;
      default:
        comparison = 0;
    }

    return comparison * direction;
  });

  return sorted;
}

// ============================================================================
// Context
// ============================================================================

const MemberDataContext = createContext<MemberDataContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface MemberDataProviderProps {
  children: ReactNode;
}

export function MemberDataProvider({ children }: MemberDataProviderProps) {
  const [state, dispatch] = useReducer(memberDataReducer, initialState);

  // ============================================================================
  // Members Loading
  // ============================================================================

  const loadMembers = useCallback(async () => {
    // Skip if already loaded or loading
    if (state.membersLoaded || state.membersLoading) {
      return;
    }

    dispatch({ type: 'MEMBERS_LOADING' });

    try {
      // Get corporate_id from localStorage
      const userData = localStorage.getItem('managerUserData');
      const corporateId = userData ? JSON.parse(userData).corporate_id : undefined;

      const apiMembers = await getMembers(corporateId);
      const members = apiMembers.map(mapApiMember).map(mapMemberToListItem);

      dispatch({
        type: 'MEMBERS_LOADED',
        payload: {
          members,
          totalCount: members.length,
        },
      });
    } catch (error) {
      console.error('Failed to load members:', error);
      dispatch({
        type: 'MEMBERS_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to load members',
      });
    }
  }, [state.membersLoaded, state.membersLoading]);

  // Auto-load members on mount
  const membersLoadAttempted = useRef(false);
  useEffect(() => {
    if (!membersLoadAttempted.current && !state.membersLoaded && !state.membersLoading) {
      membersLoadAttempted.current = true;
      loadMembers();
    }
  }, [loadMembers, state.membersLoaded, state.membersLoading]);

  const loadMoreMembers = useCallback(() => {
    if (state.pagination.hasMore) {
      dispatch({ type: 'LOAD_MORE_MEMBERS' });
    }
  }, [state.pagination.hasMore]);

  const refreshMembers = useCallback(async () => {
    dispatch({ type: 'INVALIDATE_MEMBERS' });
    membersLoadAttempted.current = false;
    // Will trigger loadMembers on next render via useEffect
  }, []);

  // ============================================================================
  // Member Details Loading (Lazy)
  // ============================================================================

  const loadMemberDetails = useCallback(
    async (memberId: number): Promise<MemberDetails | undefined> => {
      // Return cached if available
      const cached = state.memberDetailsCache.get(memberId);
      if (cached) {
        return cached;
      }

      // Skip if already loading
      if (state.loadingDetails.has(memberId)) {
        return undefined;
      }

      dispatch({ type: 'MEMBER_DETAILS_LOADING', payload: memberId });

      try {
        // Load member details
        const apiMember = await getMemberById(memberId);
        const member = mapApiMember(apiMember);

        // Create initial details with member info, vehicles/reservations not yet loaded
        const details: MemberDetails = {
          member,
          passengerVehicles: [],
          recreationalVehicles: [],
          reservations: [],
          vehiclesLoaded: false,
          reservationsLoaded: false,
        };

        dispatch({ type: 'MEMBER_DETAILS_LOADED', payload: { memberId, details } });

        return details;
      } catch (error) {
        console.error(`Failed to load member details for member ${memberId}:`, error);
        dispatch({ type: 'MEMBER_DETAILS_ERROR', payload: { memberId, error: String(error) } });
        return undefined;
      }
    },
    [state.memberDetailsCache, state.loadingDetails]
  );

  const loadMemberVehicles = useCallback(
    async (memberId: number): Promise<void> => {
      // Check if already loaded
      const cached = state.memberDetailsCache.get(memberId);
      if (cached?.vehiclesLoaded) {
        return;
      }

      try {
        const [apiPassenger, apiRecreational] = await Promise.all([
          getPassengerVehicles(memberId),
          getRecreationalVehicles(memberId),
        ]);

        const passengerVehicles = apiPassenger.map(mapApiPassengerVehicle);
        const recreationalVehicles = apiRecreational.map(mapApiRecreationalVehicle);

        dispatch({
          type: 'MEMBER_VEHICLES_LOADED',
          payload: { memberId, passengerVehicles, recreationalVehicles },
        });
      } catch (error) {
        console.error(`Failed to load vehicles for member ${memberId}:`, error);
      }
    },
    [state.memberDetailsCache]
  );

  const loadMemberReservations = useCallback(
    async (memberId: number): Promise<void> => {
      // Check if already loaded
      const cached = state.memberDetailsCache.get(memberId);
      if (cached?.reservationsLoaded) {
        return;
      }

      try {
        // Use getReservationsByMemberId directly - efficient for single member
        const apiReservations = await getReservationsByMemberId(memberId);
        const reservations = mapApiToGridReservations(apiReservations)
          .sort((a, b) => new Date(b.checkIn).getTime() - new Date(a.checkIn).getTime());

        dispatch({
          type: 'MEMBER_RESERVATIONS_LOADED',
          payload: { memberId, reservations },
        });
      } catch (error) {
        console.error(`Failed to load reservations for member ${memberId}:`, error);
      }
    },
    [state.memberDetailsCache]
  );

  const getMemberDetails = useCallback(
    (memberId: number): MemberDetails | undefined => {
      return state.memberDetailsCache.get(memberId);
    },
    [state.memberDetailsCache]
  );

  const getMember = useCallback(
    (memberId: number): MemberListItem | undefined => {
      return state.members.find((m) => m.id === memberId);
    },
    [state.members]
  );

  const isMemberDetailsLoading = useCallback(
    (memberId: number): boolean => {
      return state.loadingDetails.has(memberId);
    },
    [state.loadingDetails]
  );

  // ============================================================================
  // Filter/Sort Actions
  // ============================================================================

  const setFilters = useCallback((filters: Partial<MemberFilters>) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const setSort = useCallback((key: MemberSortKey) => {
    dispatch({ type: 'SET_SORT', payload: key });
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' });
  }, []);

  // ============================================================================
  // Cache Management
  // ============================================================================

  const invalidateMemberDetails = useCallback((memberId: number) => {
    dispatch({ type: 'INVALIDATE_MEMBER_DETAILS', payload: memberId });
  }, []);

  const invalidateAllMemberDetails = useCallback(() => {
    dispatch({ type: 'INVALIDATE_ALL_MEMBER_DETAILS' });
  }, []);

  const invalidateMemberList = useCallback(() => {
    dispatch({ type: 'INVALIDATE_MEMBERS' });
  }, []);

  // ============================================================================
  // CRUD Notifications
  // ============================================================================

  const onMemberCreated = useCallback((member: MemberListItem) => {
    dispatch({ type: 'MEMBER_CREATED', payload: member });
  }, []);

  const onMemberUpdated = useCallback((memberId: number) => {
    dispatch({ type: 'MEMBER_UPDATED', payload: memberId });
  }, []);

  const onMemberDeleted = useCallback((memberId: number) => {
    dispatch({ type: 'MEMBER_DELETED', payload: memberId });
  }, []);

  // ============================================================================
  // Computed Values
  // ============================================================================

  const { displayedMembers, filteredCount } = useMemo(() => {
    // Apply filters
    const filtered = applyFilters(state.members, state.filters);

    // Apply sort
    const sorted = applySort(filtered, state.sort);

    // Apply pagination (client-side)
    const pageEnd = state.pagination.page * state.pagination.pageSize;
    const displayed = sorted.slice(0, pageEnd);

    return {
      displayedMembers: displayed,
      filteredCount: filtered.length,
    };
  }, [state.members, state.filters, state.sort, state.pagination]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<MemberDataContextValue>(
    () => ({
      state,
      displayedMembers,
      filteredCount,
      loadMembers,
      loadMoreMembers,
      refreshMembers,
      loadMemberDetails,
      loadMemberVehicles,
      loadMemberReservations,
      getMemberDetails,
      getMember,
      isMemberDetailsLoading,
      setFilters,
      setSort,
      clearFilters,
      invalidateMemberDetails,
      invalidateAllMemberDetails,
      invalidateMemberList,
      onMemberCreated,
      onMemberUpdated,
      onMemberDeleted,
    }),
    [
      state,
      displayedMembers,
      filteredCount,
      loadMembers,
      loadMoreMembers,
      refreshMembers,
      loadMemberDetails,
      loadMemberVehicles,
      loadMemberReservations,
      getMemberDetails,
      getMember,
      isMemberDetailsLoading,
      setFilters,
      setSort,
      clearFilters,
      invalidateMemberDetails,
      invalidateAllMemberDetails,
      invalidateMemberList,
      onMemberCreated,
      onMemberUpdated,
      onMemberDeleted,
    ]
  );

  return <MemberDataContext.Provider value={value}>{children}</MemberDataContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useMemberData(): MemberDataContextValue {
  const context = useContext(MemberDataContext);
  if (!context) {
    throw new Error('useMemberData must be used within a MemberDataProvider');
  }
  return context;
}

export default MemberDataContext;
